import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps, ReactNode } from 'react';
import { cn } from '../cn.js';

/* ── Button ───────────────────────────────────────────────────────────── */

export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors duration-150 ease-(--ease-forge) disabled:pointer-events-none disabled:opacity-45 [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary: 'bg-brasa text-carbon hover:bg-[#ff8f3a] active:bg-brasa-honda',
        secondary: 'bg-forja text-hueso ring-1 ring-escoria hover:bg-escoria',
        ghost: 'text-ceniza hover:bg-forja hover:text-hueso',
        danger: 'bg-danger/15 text-danger ring-1 ring-danger/30 hover:bg-danger/25',
      },
      size: {
        sm: 'h-8 px-3',
        md: 'h-9 px-4',
        lg: 'h-11 px-5 text-[15px]',
        icon: 'size-9',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps extends ComponentProps<'button'>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export function Button({ className, variant, size, asChild, ...props }: ButtonProps) {
  const Comp = asChild ? Slot : 'button';
  return <Comp className={cn(buttonVariants({ variant, size }), className)} {...props} />;
}

/* ── Panel ────────────────────────────────────────────────────────────── */

export function Panel({ className, ...props }: ComponentProps<'section'>) {
  return (
    <section
      className={cn('rounded-lg bg-hierro ring-1 ring-escoria shadow-(--shadow-panel)', className)}
      {...props}
    />
  );
}

export function PanelHeader({
  title,
  meta,
  className,
}: {
  title: ReactNode;
  meta?: ReactNode;
  className?: string;
}) {
  return (
    <header
      className={cn(
        'flex items-center justify-between gap-3 border-b border-escoria px-5 py-3.5',
        className,
      )}
    >
      <h2 className="text-[13px] font-semibold uppercase font-condensed text-ceniza">{title}</h2>
      {meta}
    </header>
  );
}

/* ── Status ───────────────────────────────────────────────────────────── */

export type StatusTone = 'ok' | 'warn' | 'danger' | 'info' | 'idle' | 'ember';

const toneClass: Record<StatusTone, string> = {
  ok: 'text-ok bg-ok/10 ring-ok/25',
  warn: 'text-warn bg-warn/10 ring-warn/25',
  danger: 'text-danger bg-danger/10 ring-danger/25',
  info: 'text-info bg-info/10 ring-info/25',
  idle: 'text-ceniza bg-forja ring-escoria',
  ember: 'text-brasa bg-brasa-tenue ring-brasa/30',
};

const dotClass: Record<StatusTone, string> = {
  ok: 'bg-ok',
  warn: 'bg-warn',
  danger: 'bg-danger',
  info: 'bg-info',
  idle: 'bg-ceniza-2',
  ember: 'bg-brasa',
};

/** Estado siempre con texto: el color nunca es el único portador de significado. */
export function StatusPill({
  tone,
  children,
  className,
}: {
  tone: StatusTone;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ring-1',
        toneClass[tone],
        className,
      )}
    >
      <span aria-hidden className={cn('size-1.5 rounded-full', dotClass[tone])} />
      {children}
    </span>
  );
}

/* ── Kbd / Mono ───────────────────────────────────────────────────────── */

export function Mono({ className, ...props }: ComponentProps<'code'>) {
  return <code className={cn('font-mono text-[12.5px] text-ceniza', className)} {...props} />;
}
